/**
 * The PIN, asked for one action at a time (§17, §32).
 *
 * There is one template. The child's app is the app, and the things that
 * belong to a parent are buttons inside it that ask for the PIN: a progress
 * report, adding a sibling, exporting or erasing a child's data, changing the
 * PIN itself. Each one asks separately.
 *
 * That is stricter than what it replaces, which is worth saying because it
 * looks like a simplification. Before this, one PIN entry minted a parent
 * session good for thirty days and everything behind the parent area was open
 * for its whole life. Now the PIN proves the action rather than a mode.
 *
 * The honest limit, unchanged: this is not what stops a determined child. The
 * backend refuses a student session on those endpoints, counts wrong PINs per
 * parent and locks the account after five. What the client contributes is
 * that a child tapping around sees their own app instead of a 403.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { ApiError } from '@/api/errors';
import { useAuth } from '@/features/auth/AuthProvider';
import { PinDialog } from './PinDialog';

export interface PinRequest<T> {
  /** What the parent is about to do, in their language. */
  title: string;
  body?: string;
  work(): Promise<T>;
}

export interface PinGate {
  /**
   * Ask for the PIN, then run `work` with the parent's session.
   *
   * Resolves with whatever `work` returned, or `undefined` if the parent
   * cancelled. A wrong PIN never reaches the caller: it is handled inside the
   * dialog, because sending someone back to the start for a typo is how a
   * four-digit code becomes six taps. Anything `work` itself throws does
   * reach the caller.
   */
  ask<T>(request: PinRequest<T>): Promise<T | undefined>;
}

/** A PIN the parent can try again, as opposed to an operation that failed. */
export function isPinProblem(error: ApiError): boolean {
  return error.code === 'PIN_INCORRECT' || error.code === 'PIN_LOCKED';
}

/**
 * This account has no PIN, so there is nothing to ask for.
 *
 * The backend answers a parent switch on a PIN-less account with a conflict
 * -- "sign in with your number to get back to the parent area" -- which is a
 * different conversation from a wrong PIN and gets different copy. It happens
 * to parents who skipped setup, and it has to be handled rather than assumed
 * away, because asking for a PIN that does not exist is a dead end.
 */
export function noPinSet(error: ApiError): boolean {
  return error.code === 'RESOURCE_CONFLICT';
}

export interface PendingPinRequest {
  title: string;
  body?: string;
  /** Rethrows a PIN problem to the dialog; anything else goes to the caller. */
  attempt(pin: string): Promise<void>;
  cancel(): void;
}

const Context = createContext<PinGate | null>(null);

export function PinGateProvider({ children }: { children: ReactNode }) {
  const { borrowParentSession, isParent } = useAuth();
  const [pending, setPending] = useState<PendingPinRequest | null>(null);
  // One at a time: two open dialogs would both be holding a borrow.
  const inFlight = useRef(false);

  const ask = useCallback(
    <T,>(request: PinRequest<T>): Promise<T | undefined> =>
      new Promise<T | undefined>((resolve, reject) => {
        if (inFlight.current) {
          resolve(undefined);
          return;
        }

        // Already the parent: nothing to prove, so do not ask.
        //
        // This is first run -- a parent who has signed in and not yet handed
        // the phone to anybody -- and it is also the forgotten-PIN escape
        // hatch, because signing in with WhatsApp mints a parent session.
        // Both are the same statement: whoever holds a parent session already
        // has parent access, which was true before this gate existed too. The
        // gate's job is the device *after* it has been handed over, which is
        // every device with a child on it.
        if (isParent) {
          void (async () => {
            try {
              resolve(await request.work());
            } catch (cause) {
              reject(cause);
            }
          })();
          return;
        }

        inFlight.current = true;

        const close = () => {
          inFlight.current = false;
          setPending(null);
        };

        setPending({
          title: request.title,
          ...(request.body === undefined ? {} : { body: request.body }),
          attempt: async (pin: string) => {
            let value: T;
            try {
              value = await borrowParentSession(pin, request.work);
            } catch (cause) {
              const error = cause as ApiError;
              // Anything about the PIN itself stays in the dialog: a typo to
              // correct, a lockout to read, or an account that has no PIN to
              // be told about. Anything else is the operation failing, and
              // that is the caller's to render -- they know what they asked
              // for. `noPinSet` belongs on this side of the line even though
              // it is not a wrong PIN, because the dialog is the only place
              // that can say "there is nothing to type".
              if (error && (isPinProblem(error) || noPinSet(error))) throw cause;
              close();
              reject(cause);
              return;
            }
            close();
            resolve(value);
          },
          cancel: () => {
            close();
            resolve(undefined);
          },
        });
      }),
    [borrowParentSession, isParent],
  );

  const value = useMemo<PinGate>(() => ({ ask }), [ask]);

  return (
    <Context.Provider value={value}>
      {children}
      {pending ? <PinDialog request={pending} /> : null}
    </Context.Provider>
  );
}

/**
 * Exported for the test harness only.
 *
 * `renderScreen` wraps every screen in a gate that runs the work without a
 * dialog, because those tests are about the forms and the reports -- the PIN
 * itself has its own tests, which render the real provider. Without this,
 * every test touching a screen with a parent action had to know about the
 * dialog, and a test that does not care about the PIN should not have to type
 * one.
 */
export const PinGateContextForTests = Context;

export function usePinGate(): PinGate {
  const gate = useContext(Context);
  if (!gate) throw new Error('usePinGate must be used inside <PinGateProvider>');
  return gate;
}
